import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import classNames from "classnames"

import { useAppDispatch } from "store/hooks"
import { showAccountSettingsModal } from "store/modalSlise"
import { ReactComponent as IconLogout } from "assets/svg/icon-logout.svg"
import { ReactComponent as LockClosed } from "assets/svg/lock-closed.svg"
import { ReactComponent as IconAccountSettings } from "assets/svg/modal-icon-account-settings.svg"
import { ReactComponent as UserAvatar } from "assets/svg/user-avatar.svg"
import { ReactComponent as Users } from "assets/svg/users.svg"

import { checkRoleType } from "utils/checkRoleType"
import { useLogout } from "hooks/useLogout"
import { useUserProfile } from "hooks/useUserProfile"

import styles from "./index.module.scss"

export const UserMenu = () => {
  const dispatch = useAppDispatch()
  const logout = useLogout()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { userProfile, userRole, isEnterprise, isContractor } = useUserProfile()
  const isAccountPage = pathname.startsWith("/account")
  const isMasterPassAllowed = [
    "vendor-admin",
    "sales-manager",
    "contractor-admin",
    "contractor-manager",
    "contractor-engineer",
    "contractor-technician",
  ].includes(checkRoleType(userRole) || "")
  const userManagementAllowed = ["contractor-manager", "contractor-admin"].includes(checkRoleType(userRole) || "")
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  let timeoutId: ReturnType<typeof setTimeout>

  const openDropdown = () => {
    clearTimeout(timeoutId)
    setDropdownVisible(true)
  }

  const closeDropdownOnLeave = () => {
    timeoutId = setTimeout(() => {
      setDropdownVisible(false)
    }, 300)
  }

  const closeDropdownOnClick = () => {
    setDropdownVisible(false)
  }

  const openAccountSettings = () => {
    if (isEnterprise) {
      navigate("/account")
      setDropdownVisible(false)
      return
    }

    if (isContractor) {
      dispatch(showAccountSettingsModal(true))
      return
    }
  }

  if (isEnterprise) {
    return (
      <div
        className={styles.userMenuWrapper}
        onMouseEnter={openDropdown}
        onMouseLeave={closeDropdownOnLeave}
        onTouchStart={openDropdown}
      >
        <figure className={classNames(styles.userMenuButton, { [styles.active]: isDropdownVisible })}>
          <UserAvatar className={styles.userAvatarWhite} />
        </figure>
        <div className={classNames(styles.userDropdown, { [styles.hidden]: !isDropdownVisible })}>
          <div className={classNames(styles.userDropdownItem)} onClick={openAccountSettings}>
            <UserAvatar className={classNames(styles.itemIcon, styles.userAvatarBlack)} />
            <div>
              <div className={styles.userName}>{userProfile?.name}</div>
              <div className={styles.userEmail}>{userProfile?.email}</div>
            </div>
          </div>
          <div className={styles.userDropdownItem} onClick={logout} data-testid="user-menu-logout">
            <IconLogout className={styles.itemIcon} />
            Log Out
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.userMenuWrapper}
      onMouseEnter={openDropdown}
      onMouseLeave={closeDropdownOnLeave}
      onTouchStart={openDropdown}
    >
      <figure className={classNames(styles.userMenuButton, { [styles.active]: isDropdownVisible })}>
        <UserAvatar className={styles.userAvatarWhite} />
      </figure>
      <div className={classNames(styles.userDropdown, { [styles.hidden]: !isDropdownVisible })}>
        {isAccountPage && (
          <Link to="/account/profile" className={classNames(styles.userDropdownItem, styles.accountLink)}>
            <IconAccountSettings className={styles.itemIcon} />
            Account
          </Link>
        )}
        <div
          className={classNames(styles.userDropdownItem, {
            [styles.skipPointer]: isAccountPage || (userProfile?.type !== 4 && userProfile?.type !== 9),
          })}
          onClick={isAccountPage ? undefined : openAccountSettings}
        >
          <UserAvatar className={classNames(styles.itemIcon, styles.userAvatarBlack)} />
          <div>
            <div className={styles.userName}>{userProfile?.username}</div>
            <div className={styles.userEmail}>{userProfile?.email}</div>
          </div>
        </div>
        {!isAccountPage && userManagementAllowed && (
          <Link to="/users" className={styles.userDropdownItem} onClick={closeDropdownOnClick}>
            <Users className={styles.itemIcon} />
            User Management
          </Link>
        )}
        {!isAccountPage && isMasterPassAllowed && (
          <Link to="/master-pass" className={styles.userDropdownItem} onClick={closeDropdownOnClick}>
            <LockClosed className={styles.itemIcon} />
            Master Pass
          </Link>
        )}
        <div className={styles.userDropdownItem} onClick={logout} data-testid="user-menu-logout">
          <IconLogout className={styles.itemIcon} />
          Log Out
        </div>
      </div>
    </div>
  )
}
